import {
  settingsMenuConfig,
  type SettingsMenuContext,
  type SettingsMenuItemConfig,
} from './settings-menu.config';

import type { SettingsSearchEntry } from './settings-search.config';

export interface SettingsSearchGroup {
  sectionId: string;
  sectionTitle: string;
  /** The section title itself matched, so the section is offered as a result */
  sectionMatched: boolean;
  entries: SettingsSearchEntry[];
}

// Match-quality tiers, strongest first. Fuzzy fallbacks (compact,
// subsequence, typo) sit below every literal tier so exact text always wins
const SCORE_EXACT = 1;
const SCORE_PREFIX = 0.9;
const SCORE_WORD_START = 0.8;
const SCORE_SUBSTRING = 0.7;
// "menubar" ↔ "menu bar": query and text match once spacing/punctuation is
// stripped from both sides
const SCORE_COMPACT = 0.65;
const SCORE_SUBSEQUENCE = 0.5;
const SCORE_TYPO = 0.45;

// Multi-word queries may match word-by-word in any order; keep that below a
// same-order phrase hit of equal quality
const MULTI_WORD_FACTOR = 0.8;

// Where the match landed: labels beat keywords beat descriptions on ties
const WEIGHT_LABEL = 1;
const WEIGHT_KEYWORD = 0.95;
const WEIGHT_DESCRIPTION = 0.9;

// 1-2 char queries stay literal — fuzzy tiers would match almost everything
const MIN_FUZZY_QUERY_LENGTH = 3;

/** Shared query normalization: trimmed, lowercased, whitespace collapsed */
export const normalizeSettingsSearchQuery = (query: string): string =>
  query.trim().toLowerCase().replace(/\s+/g, ' ');

// Words are letter/digit-led runs in any script (Unicode property escapes),
// so non-Latin queries (CJK, Cyrillic, ...) tokenize instead of dissolving
// into zero words. \p{M} keeps mark-using scripts (Devanagari, Thai, Arabic
// diacritics, ...) whole: without it a vowel sign like ि (U+093F) splits
// "परियोजना" into fragments. Marks only continue a word, never start one, so
// a free-standing mark like the emoji variation selector ️ (U+FE0F) is
// dropped instead of surviving as a stray token
const splitWords = (text: string): string[] =>
  text.match(/[\p{L}\p{N}][\p{L}\p{M}\p{N}]*/gu) ?? [];

const compactText = (text: string): string => splitWords(text).join('');

const isSubsequence = (needle: string, haystack: string): boolean => {
  let matchedLength = 0;
  for (const char of haystack) {
    if (char === needle[matchedLength]) matchedLength++;
  }
  return matchedLength === needle.length;
};

/** Typos tolerated for a query word: none under 4 chars, 2 from 8 chars */
const typoBudget = (queryWord: string): number =>
  queryWord.length >= 8 ? 2 : queryWord.length >= 4 ? 1 : 0;

/**
 * Damerau-Levenshtein distance (insert / delete / substitute / adjacent
 * transposition) capped at `max`, bailing out early once a row can no longer
 * come back under the cap
 */
const isWithinEditDistance = (a: string, b: string, max: number): boolean => {
  if (Math.abs(a.length - b.length) > max) return false;
  let twoRowsBack: number[] = [];
  let previousRow = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const currentRow = [i];
    let rowMinimum = i;
    for (let j = 1; j <= b.length; j++) {
      let distance = Math.min(
        previousRow[j] + 1,
        currentRow[j - 1] + 1,
        previousRow[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
      );
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        distance = Math.min(distance, twoRowsBack[j - 2] + 1);
      }
      currentRow.push(distance);
      rowMinimum = Math.min(rowMinimum, distance);
    }
    if (rowMinimum > max) return false;
    twoRowsBack = previousRow;
    previousRow = currentRow;
  }
  return previousRow[b.length] <= max;
};

const matchesWithTypo = (queryWord: string, textWords: string[]): boolean => {
  const budget = typoBudget(queryWord);
  return (
    budget > 0 &&
    textWords.some((word) => isWithinEditDistance(queryWord, word, budget))
  );
};

const startsAtWordBoundary = (text: string, query: string): boolean => {
  for (
    let index = text.indexOf(query);
    index !== -1;
    index = text.indexOf(query, index + 1)
  ) {
    // The anchored test reads the last full code point before the match;
    // text[index - 1] would be a single UTF-16 code unit, and the lone
    // trailing surrogate of a supplementary-plane letter (e.g. "𐐷") fails
    // \p{L}, faking a word boundary. Matching splitWords, marks extend a
    // word only when they trail a base letter/digit
    if (!/[\p{L}\p{N}]\p{M}*$/u.test(text.slice(0, index))) return true;
  }
  return false;
};

interface PreparedQuery {
  full: string;
  compact: string;
  words: string[];
}

const prepareQuery = (query: string): PreparedQuery => {
  const full = normalizeSettingsSearchQuery(query);
  return { full, compact: compactText(full), words: splitWords(full) };
};

/** Best score of one query word against the individual words of a text */
const scoreQueryWord = (
  queryWord: string,
  textWords: string[],
  allowSubsequence: boolean
): number => {
  let best = 0;
  for (const word of textWords) {
    if (word === queryWord) return SCORE_EXACT;
    if (word.startsWith(queryWord)) best = Math.max(best, SCORE_PREFIX);
    else if (word.includes(queryWord)) best = Math.max(best, SCORE_SUBSTRING);
    else if (
      allowSubsequence &&
      queryWord.length >= MIN_FUZZY_QUERY_LENGTH &&
      isSubsequence(queryWord, word)
    )
      best = Math.max(best, SCORE_SUBSEQUENCE);
    else if (matchesWithTypo(queryWord, [word]))
      best = Math.max(best, SCORE_TYPO);
  }
  return best;
};

/**
 * 0 = no match; higher = better match. Pure and deterministic.
 *
 * `allowSubsequence` gates the subsequence tier: over short texts (labels,
 * keywords, section titles) it catches abbreviations like "clrmode", but over
 * description prose almost any query threads letter-by-letter through the
 * text ("lienar" through "...caLendar events In thE macos meNu bAR..."),
 * surfacing junk rows above genuine typo hits — so descriptions turn it off
 */
const scoreText = (
  query: PreparedQuery,
  text: string,
  allowSubsequence = true
): number => {
  const normalizedText = text.toLowerCase();
  if (normalizedText === query.full) return SCORE_EXACT;
  if (normalizedText.startsWith(query.full)) return SCORE_PREFIX;
  if (normalizedText.includes(query.full)) {
    return startsAtWordBoundary(normalizedText, query.full)
      ? SCORE_WORD_START
      : SCORE_SUBSTRING;
  }

  const compacted = compactText(normalizedText);
  if (query.compact && compacted.includes(query.compact)) return SCORE_COMPACT;

  const textWords = splitWords(normalizedText);
  if (query.words.length === 1) {
    const queryWord = query.words[0];
    if (
      allowSubsequence &&
      queryWord.length >= MIN_FUZZY_QUERY_LENGTH &&
      isSubsequence(queryWord, compacted)
    ) {
      return SCORE_SUBSEQUENCE;
    }
    return matchesWithTypo(queryWord, textWords) ? SCORE_TYPO : 0;
  }

  let weakestWordScore = SCORE_EXACT;
  for (const queryWord of query.words) {
    const wordScore = scoreQueryWord(queryWord, textWords, allowSubsequence);
    if (wordScore === 0) return 0;
    weakestWordScore = Math.min(weakestWordScore, wordScore);
  }
  return weakestWordScore * MULTI_WORD_FACTOR;
};

export interface SettingsSearchText {
  label: string;
  keywords?: string[];
  description?: string;
}

const scoreEntry = (
  entry: SettingsSearchText,
  query: PreparedQuery
): number => {
  let best = scoreText(query, entry.label) * WEIGHT_LABEL;
  for (const keyword of entry.keywords ?? []) {
    best = Math.max(best, scoreText(query, keyword) * WEIGHT_KEYWORD);
  }
  if (entry.description) {
    // literal tiers only — see the subsequence note on scoreText
    best = Math.max(
      best,
      scoreText(query, entry.description, false) * WEIGHT_DESCRIPTION
    );
  }
  return best;
};

const NO_BOOSTS: ReadonlyMap<string, number> = new Map();

/**
 * Fuzzy, typo-tolerant search over section titles and manifest entries,
 * gated by `ctx` the same way the sidebar menu is. Entries rank by match
 * quality within their group (exact > prefix > word-start > substring >
 * spacing-insensitive > subsequence > typo, and label > keywords >
 * description; subsequence applies to labels, keywords, and section titles
 * only) and groups order by their best hit.
 *
 * `boosts` (entry id → acceptance weight, see `settings-search.acceptance`)
 * breaks ties only between identical fuzzy scores — both among a group's
 * entries and between groups whose best hits tie — so a boost can never lift
 * a weaker match above a stronger one. Remaining ties keep sidebar /
 * manifest order
 */
export const searchSettings = (
  query: string,
  entries: SettingsSearchEntry[],
  ctx: SettingsMenuContext,
  sections: SettingsMenuItemConfig[] = settingsMenuConfig,
  boosts: ReadonlyMap<string, number> = NO_BOOSTS
): SettingsSearchGroup[] => {
  const preparedQuery = prepareQuery(query);
  // No letter/number content (empty, punctuation-only, emoji-only) can't match
  // anything — and would otherwise fall through to the multi-word path with
  // zero words, scoring every text as a match
  if (preparedQuery.words.length === 0) return [];

  return sections
    .flatMap((section) => {
      if (section.enabled && !section.enabled(ctx)) return [];

      const sectionId = section.hash.replace('settings/', '');
      const sectionScore = scoreText(preparedQuery, section.title);
      const scoredEntries = entries
        .filter(
          (entry) =>
            entry.sectionId === sectionId &&
            (!entry.enabled || entry.enabled(ctx))
        )
        .map((entry) => ({ entry, score: scoreEntry(entry, preparedQuery) }))
        .filter(({ score }) => score > 0)
        // sort is stable, so full ties keep manifest order
        .sort(
          (a, b) =>
            b.score - a.score ||
            (boosts.get(b.entry.id) ?? 0) - (boosts.get(a.entry.id) ?? 0)
        );

      if (sectionScore === 0 && scoredEntries.length === 0) return [];
      const bestScore = Math.max(sectionScore, scoredEntries[0]?.score ?? 0);
      return [
        {
          bestScore,
          bestBoost: Math.max(
            0,
            ...scoredEntries
              .filter(({ score }) => score === bestScore)
              .map(({ entry }) => boosts.get(entry.id) ?? 0)
          ),
          group: {
            sectionId,
            sectionTitle: section.title,
            sectionMatched: sectionScore > 0,
            entries: scoredEntries.map(({ entry }) => entry),
          },
        },
      ];
    })
    .sort((a, b) => b.bestScore - a.bestScore || b.bestBoost - a.bestBoost)
    .map(({ group }) => group);
};

export const scoreSettingsEntry = (
  entry: SettingsSearchText,
  query: string
): number => {
  const preparedQuery = prepareQuery(query);
  if (preparedQuery.words.length === 0) return 0;
  return scoreEntry(entry, preparedQuery);
};
